import type { WithRelationsMatch } from '@shared-types/bracket.types';
import type { Order } from '@shared-types/common.types';
import type {
  Lineups,
  Match,
  MatchStatus,
  MatchType,
  PlayerInfo,
  RoundMode,
  RoundPhase,
} from '@shared-types/match.types';
import { loadFromAPIs, recursiveCall } from '@utilities/fetch';
import { apiKey, domesticApiUrl, matchApiUrl, pssApiUrl } from '@utilities/WindowContext';

export type MatchApiParams = {
  isDomestic?: boolean;
  competitionId?: string | string[];
  matchId?: string[];
  seasonYear?: string;
  seasonsRange?: 'HISTORICAL' | 'ALL' | string;
  fromDate?: string;
  toDate?: string;
  utcOffset?: number;
  kickOffDistance?: number;
  roundId?: string | string[];
  matchDayId?: string | string[];
  matchType?: MatchType | MatchType[];
  groupId?: string;
  roundMode?: RoundMode;
  phase?: RoundPhase | 'ALL';
  teamId?: string | string[];
  opponentTeamIds?: [string, string];
  status?: MatchStatus | MatchStatus[];
  stadiumId?: string;
  playerIds?: string[];
  limit?: number;
  offset?: number;
  order?: Order;
};

export const fetchMatches = (params: MatchApiParams, signal?: AbortSignal) => {
  if (params.isDomestic) {
    return fetchDomesticMatches(
      {
        matchIds: params.matchId,
        competitionId: params.competitionId,
        seasonYear: params.seasonYear,
        status: params.status,
        teamId: params.teamId,
        fromDate: params.fromDate,
        toDate: params.toDate,
        offset: params.offset,
        limit: params.limit,
        order: params.order,
      },
      signal,
    );
  }

  return recursiveCall<Match>('v5/matches', params, { apiUrl: matchApiUrl, apiKey, signal });
};

export const fetchMatchesById = (matchIds: string[], signal?: AbortSignal) =>
  loadFromAPIs<Match[]>('v5/matches', { matchId: matchIds }, { apiUrl: matchApiUrl, apiKey, signal });

export type DomesticMatchApiParams = {
  matchIds?: string[];
  competitionId?: string | string[];
  seasonYear?: string;
  status?: MatchStatus | MatchStatus[];
  teamId?: string | string[];
  fromDate?: string;
  toDate?: string;
  offset?: number;
  limit?: number;
  order?: Order;
};

const fetchDomesticMatches = (params: DomesticMatchApiParams, signal?: AbortSignal) =>
  recursiveCall<Match>('v2/matches', params, {
    apiUrl: domesticApiUrl,
    apiKey,
    signal,
  });

export const fetchDomesticMatchesById = (matchIds?: string[], signal?: AbortSignal) =>
  loadFromAPIs<Match[]>('v2/matches', { matchIds }, { apiUrl: domesticApiUrl, apiKey, signal });

export const fetchMatch = (matchId: string, signal?: AbortSignal) =>
  loadFromAPIs<Match>(`v5/matches/${matchId}`, {}, { apiKey, apiUrl: matchApiUrl, signal });

export const fetchLineups = (matchId: string, signal?: AbortSignal) =>
  loadFromAPIs<Lineups>(`v5/matches/${matchId}/lineups`, {}, { apiKey, apiUrl: matchApiUrl, signal });

export const getPlayersStatus = (matchId: string, status: string | string[], signal?: AbortSignal) =>
  loadFromAPIs<{ players: PlayerInfo[] }>(
    'v3/playerstatus',
    {
      matchId,
      status,
    },
    { apiKey, apiUrl: pssApiUrl, signal },
  ).then((res) => res?.players);

export const fetchEuroBracket = (params: { competitionId: string; seasonYear: string }, signal?: AbortSignal) =>
  loadFromAPIs<{ data: WithRelationsMatch[] }>('v5/matches/bracket', params, { apiKey, apiUrl: matchApiUrl, signal });
